'use client';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedLogoIcon } from '@/_components/AnimatedLogoIcon';
import { getProductSummary, submitProductQuestionAction } from '@/app/actions/productDetails';
import { fetchStoreConfigAction } from '@/lib/server/actions';
import { AddToCart, BuyNowButton } from '@/_components/buy.template';

/* ── Section title label ──────────────────────────────────────────────────── */
const SLabel = ({ text }) => (
    <p style={{ margin: '0 0 0.55rem', fontSize: '0.68rem', fontWeight: 700, color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {text}
    </p>
);

const Wrap = ({ children, top = true }) => (
    <div style={{ borderTop: top ? '1px solid #f1f5f9' : 'none', paddingTop: top ? '1rem' : 0 }}>
        {children}
    </div>
);

/* ── pricing summary ──────────────────────────────────────────────────────── */
const PricingSection = ({ price, mrp, offer, coupons, codCharges = 0, prepaidDiscount = 0, warranty }) => {
    if (!price) return null;
    const fmt          = (n) => Number(n).toLocaleString('en-IN');
    const discount     = mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
    const savings      = mrp && mrp > price ? mrp - price : 0;
    const codFinal     = price + codCharges;
    const prepaidFinal = Math.max(0, price - prepaidDiscount);

    const couponLabel = (c) => {
        if (c.discountType === 'percentage') return `${c.discountValue}% OFF`;
        if (c.discountType === 'fixed')      return `₹${fmt(c.discountValue)} OFF`;
        return `${c.discountValue} OFF`;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>

            {/* ── Base price card ── */}
            <div style={{
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                borderRadius: '12px', padding: '0.85rem 1rem',
                border: '1px solid #bbf7d0',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>₹{fmt(price)}</span>
                    {discount > 0 && (
                        <span style={{ background: '#22c55e', color: '#fff', fontSize: '0.68rem', fontWeight: 700, padding: '3px 9px', borderRadius: '20px' }}>
                            {discount}% OFF
                        </span>
                    )}
                </div>
                {mrp && mrp > price && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.25rem' }}>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8', textDecoration: 'line-through' }}>MRP ₹{fmt(mrp)}</span>
                        <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>You save ₹{fmt(savings)}</span>
                    </div>
                )}
                {offer && typeof offer === 'string' && (
                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#475569', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                        <i className="pi pi-tag" style={{ fontSize: '0.6rem', color: '#22c55e' }} />{offer}
                    </p>
                )}
            </div>

            {/* ── COD vs Prepaid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>

                {/* COD */}
                <div style={{
                    borderRadius: '10px', padding: '0.75rem 0.9rem',
                    border: '1px solid #e2e8f0', background: '#fff',
                    display: 'flex', flexDirection: 'column', gap: '0.2rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.2rem' }}>
                        <i className="pi pi-wallet" style={{ fontSize: '0.65rem', color: '#64748b' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cash on Delivery</span>
                    </div>
                    <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>₹{fmt(codFinal)}</span>
                    {codCharges > 0
                        ? <span style={{ fontSize: '0.7rem', color: '#f97316' }}>+₹{fmt(codCharges)} COD fee</span>
                        : <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: 600 }}>✓ Free COD</span>
                    }
                </div>

                {/* Prepaid */}
                <div style={{
                    borderRadius: '10px', padding: '0.75rem 0.9rem',
                    border: '1.5px solid var(--primary-color)',
                    background: 'rgba(11,49,99,0.03)',
                    display: 'flex', flexDirection: 'column', gap: '0.2rem',
                    position: 'relative',
                }}>
                    {prepaidDiscount > 0 && (
                        <span style={{
                            position: 'absolute', top: -9, right: 8,
                            background: 'var(--primary-color)', color: '#fff',
                            fontSize: '0.58rem', fontWeight: 700,
                            padding: '2px 7px', borderRadius: '10px', letterSpacing: '0.04em',
                        }}>BEST PRICE</span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.2rem' }}>
                        <i className="pi pi-credit-card" style={{ fontSize: '0.65rem', color: 'var(--primary-color)' }} />
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--primary-color)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Online / Prepaid</span>
                    </div>
                    <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>₹{fmt(prepaidFinal)}</span>
                    {prepaidDiscount > 0
                        ? <span style={{ fontSize: '0.7rem', color: '#16a34a', fontWeight: 600 }}>−₹{fmt(prepaidDiscount)} prepaid discount</span>
                        : <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>No extra charges</span>
                    }
                </div>
            </div>

            {/* ── Coupons ── */}
            {coupons?.length > 0 && (
                <div style={{
                    background: '#fefce8', border: '1px solid #fde68a',
                    borderRadius: '10px', padding: '0.7rem 0.9rem',
                }}>
                    <p style={{ margin: '0 0 0.45rem', fontSize: '0.65rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <i className="pi pi-ticket" style={{ fontSize: '0.65rem' }} />Available Coupons
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {coupons.map((c, i) => (
                            <div key={i} style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                                background: '#fff', border: '1.5px dashed #f59e0b',
                                borderRadius: '6px', padding: '0.25rem 0.65rem',
                            }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', letterSpacing: '0.05em' }}>{c.code}</span>
                                <span style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 600 }}>{couponLabel(c)}</span>
                            </div>
                        ))}
                    </div>
                    <p style={{ margin: '0.45rem 0 0', fontSize: '0.62rem', color: '#a16207', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <i className="pi pi-check-circle" style={{ fontSize: '0.6rem', color: '#16a34a' }} />
                        Auto apply at checkout · No need to copy
                    </p>
                </div>
            )}

            {/* ── Warranty ── */}
            {warranty && (
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.55rem',
                    background: warranty.duration > 0 ? '#f0fdf4' : '#f8fafc',
                    border: `1px solid ${warranty.duration > 0 ? '#bbf7d0' : '#e2e8f0'}`,
                    borderRadius: '10px', padding: '0.65rem 0.85rem',
                }}>
                    <i
                        className={`pi ${warranty.duration > 0 ? 'pi-shield' : 'pi-info-circle'}`}
                        style={{ fontSize: '0.8rem', color: warranty.duration > 0 ? '#16a34a' : '#94a3b8', flexShrink: 0, marginTop: 2 }}
                    />
                    <div>
                        <p style={{ margin: '0 0 0.2rem', fontSize: '0.68rem', fontWeight: 700, color: warranty.duration > 0 ? '#15803d' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {warranty.duration > 0 ? `${warranty.duration} ${warranty.time} Warranty` : 'Warranty'}
                        </p>
                        {warranty.info && (
                            <p style={{ margin: 0, fontSize: '0.72rem', color: '#475569', lineHeight: 1.55 }}>
                                {warranty.info}
                            </p>
                        )}
                        {warranty.duration > 0 && (
                            <p style={{ margin: '0.3rem 0 0', fontSize: '0.7rem', color: '#475569', lineHeight: 1.5, fontStyle: 'italic' }}>
                                Please note that this accessory is eligible for a {warranty.duration}-{warranty.time} warranty only.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ── hero: intro banner ───────────────────────────────────────────────────── */
const HeroSection = ({ s }) => (
    <div style={{
        background: 'linear-gradient(135deg, rgba(11,49,99,0.05) 0%, rgba(11,49,99,0.02) 100%)',
        borderLeft: '3px solid var(--primary-color)',
        borderRadius: '0 10px 10px 0',
        padding: '0.9rem 1rem',
    }}>
        <SLabel text={s.title} />
        <p style={{ margin: 0, fontSize: '0.87rem', color: '#1e293b', lineHeight: 1.8 }}>{s.content}</p>
    </div>
);

/* ── feature_highlights: pill grid ───────────────────────────────────────── */
const FeatureHighlightsSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {(s.content || []).map((item, i) => (
                <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0.3rem 0.75rem', borderRadius: '20px',
                    background: 'rgba(11,49,99,0.06)', border: '1px solid rgba(11,49,99,0.14)',
                    fontSize: '0.77rem', color: '#0b3163', fontWeight: 500,
                }}>
                    <i className="pi pi-check" style={{ fontSize: '0.6rem', color: '#22c55e' }} />
                    {item}
                </span>
            ))}
        </div>
    </Wrap>
);

/* ── overview: clean paragraph ───────────────────────────────────────────── */
const OverviewSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <p style={{ margin: 0, fontSize: '0.86rem', color: '#334155', lineHeight: 1.8 }}>{s.content}</p>
    </Wrap>
);

/* ── key_features: {title, description} list ─────────────────────────────── */
const KeyFeaturesSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
            {(s.content || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                    <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: 'var(--primary-color)', flexShrink: 0, marginTop: 7,
                    }} />
                    <div>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>{item.title}: </span>
                        <span style={{ fontSize: '0.82rem', color: '#475569' }}>{item.description}</span>
                    </div>
                </div>
            ))}
        </div>
    </Wrap>
);

/* ── indoor_uses / outdoor_uses: 2-col icon list ─────────────────────────── */
const UsesSection = ({ s, icon }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 0.5rem' }}>
            {(s.content || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8rem', color: '#475569' }}>
                    <i className={`pi ${icon}`} style={{ fontSize: '0.6rem', color: '#94a3b8', flexShrink: 0 }} />
                    {item}
                </div>
            ))}
        </div>
    </Wrap>
);

/* ── benefits: 2-column grouped cards ────────────────────────────────────── */
const BenefitsSection = ({ s }) => {
    const content = s.content || {};
    return (
        <Wrap>
            <SLabel text={s.title} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                {Object.entries(content).map(([key, items]) => (
                    <div key={key} style={{
                        background: '#f8fafc', borderRadius: '10px',
                        padding: '0.65rem 0.75rem', border: '1px solid #e2e8f0',
                    }}>
                        <p style={{ margin: '0 0 0.4rem', fontSize: '0.67rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {key.replace(/_/g, ' ')}
                        </p>
                        {(items || []).map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: '0.3rem', alignItems: 'flex-start', fontSize: '0.78rem', color: '#334155', marginBottom: 3 }}>
                                <i className="pi pi-check" style={{ fontSize: '0.58rem', color: '#22c55e', flexShrink: 0, marginTop: 4 }} />
                                {item}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </Wrap>
    );
};

/* ── technology: description + spec table ────────────────────────────────── */
const TechnologySection = ({ s }) => {
    const c = s.content || {};
    return (
        <Wrap>
            <SLabel text={s.title} />
            {c.description && (
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.84rem', color: '#334155', lineHeight: 1.75 }}>{c.description}</p>
            )}
            {c.performance && (
                <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    {c.performance.map((row, i) => (
                        <div key={i} style={{
                            display: 'flex', gap: 0,
                            background: i % 2 === 0 ? '#f8fafc' : '#fff',
                        }}>
                            <div style={{ padding: '0.4rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, color: '#475569', width: '42%', borderRight: '1px solid #e2e8f0' }}>
                                {row.feature}
                            </div>
                            <div style={{ padding: '0.4rem 0.7rem', fontSize: '0.78rem', color: '#0f172a' }}>
                                {row.value}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Wrap>
    );
};

/* ── best_fit_for: colorful tag chips ────────────────────────────────────── */
const BestFitSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {(s.content || []).map((item, i) => (
                <span key={i} style={{
                    padding: '0.22rem 0.65rem', borderRadius: '20px',
                    fontSize: '0.75rem', fontWeight: 500,
                    background: '#fff', border: '1.5px solid #e2e8f0', color: '#334155',
                }}>
                    {item}
                </span>
            ))}
        </div>
    </Wrap>
);

/* ── box_contents: package list ──────────────────────────────────────────── */
const BoxContentsSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.38rem' }}>
            {(s.content || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.82rem', color: '#334155' }}>
                    <i className="pi pi-box" style={{ fontSize: '0.65rem', color: 'var(--primary-color)', flexShrink: 0 }} />
                    {item}
                </div>
            ))}
        </div>
    </Wrap>
);

/* ── why_choose_ennix: green check 2-col ─────────────────────────────────── */
const WhyChooseSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem 0.5rem' }}>
            {(s.content || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.79rem', color: '#334155' }}>
                    <i className="pi pi-check-circle" style={{ fontSize: '0.7rem', color: '#22c55e', flexShrink: 0 }} />
                    {item}
                </div>
            ))}
        </div>
    </Wrap>
);

/* ── quick_verdict: highlighted card ─────────────────────────────────────── */
const VerdictSection = ({ s }) => (
    <Wrap>
        <SLabel text={s.title} />
        <div style={{
            background: 'linear-gradient(135deg, rgba(11,49,99,0.07) 0%, rgba(11,49,99,0.02) 100%)',
            borderRadius: '10px', padding: '0.9rem 1rem',
            border: '1px solid rgba(11,49,99,0.12)',
        }}>
            <div style={{ display: 'flex', gap: '2px', marginBottom: '0.5rem' }}>
                {[1,2,3,4,5].map(n => <span key={n} style={{ color: '#f59e0b', fontSize: '0.9rem' }}>★</span>)}
            </div>
            <p style={{ margin: 0, fontSize: '0.86rem', color: '#1e293b', lineHeight: 1.75, fontStyle: 'italic' }}>
                "{s.content}"
            </p>
        </div>
    </Wrap>
);

/* ── ai_summary: mini dashboard ──────────────────────────────────────────── */
const AISummarySection = ({ s }) => {
    const c = s.content || {};
    return (
        <Wrap>
            <SLabel text={s.title} />
            {c.overall_rating && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    background: 'rgba(11,49,99,0.05)', borderRadius: '8px',
                    padding: '0.5rem 0.8rem', marginBottom: '0.6rem',
                    border: '1px solid rgba(11,49,99,0.1)',
                }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>Overall Rating</span>
                    <span style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--primary-color)' }}>{c.overall_rating}</span>
                    <span style={{ color: '#f59e0b', fontSize: '0.85rem', letterSpacing: 1 }}>★★★★★</span>
                </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {Object.entries(c).filter(([k]) => k !== 'overall_rating').map(([key, items]) => (
                    <div key={key} style={{
                        background: '#f8fafc', borderRadius: '8px',
                        padding: '0.55rem 0.7rem', border: '1px solid #e2e8f0',
                    }}>
                        <p style={{ margin: '0 0 0.35rem', fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {key.replace(/_/g, ' ')}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                            {(items || []).map((item, i) => (
                                <span key={i} style={{
                                    fontSize: '0.71rem', color: '#334155',
                                    background: '#fff', border: '1px solid #e2e8f0',
                                    borderRadius: '4px', padding: '1px 6px',
                                }}>{item}</span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Wrap>
    );
};

/* ── fallback ─────────────────────────────────────────────────────────────── */
const DefaultSection = ({ s }) => {
    const c = s.content;
    return (
        <Wrap>
            <SLabel text={s.title} />
            {typeof c === 'string' && (
                <p style={{ margin: 0, fontSize: '0.86rem', color: '#334155', lineHeight: 1.75 }}>{c}</p>
            )}
            {Array.isArray(c) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {c.map((item, i) => (
                        <div key={i} style={{ fontSize: '0.82rem', color: '#475569' }}>
                            {typeof item === 'string' ? `• ${item}` : JSON.stringify(item)}
                        </div>
                    ))}
                </div>
            )}
        </Wrap>
    );
};

/* ── user manual (PDF) ───────────────────────────────────────────────────── */
const UserManualSection = ({ s, slug }) => (
    <Wrap>
        <SLabel text={s.title || 'User Manual'} />
        <a
            href={slug ? `/user-manual/${slug}` : s.content}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.75rem 0.9rem', borderRadius: '10px',
                border: '1px solid #e2e8f0', background: '#f8fafc',
                textDecoration: 'none', transition: 'all 0.15s',
            }}
        >
            <div style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                background: '#fee2e2', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
            }}>
                <i className="pi pi-file-pdf" style={{ color: '#dc2626', fontSize: '1rem' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
                    {slug ? 'View User Manual' : 'Download User Manual'}
                </p>
                <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b' }}>PDF document</p>
            </div>
            <i className={`pi ${slug ? 'pi-external-link' : 'pi-download'}`} style={{ color: 'var(--primary-color)', fontSize: '0.85rem', flexShrink: 0 }} />
        </a>
    </Wrap>
);

/* ── section ordering ────────────────────────────────────────────────────── */
// user_manual sits first so it renders directly below the Pricing block.
const SECTION_ORDER = [
    'user_manual',
    'feature_highlights',
    'best_fit_for',
    'key_features',
    'indoor_uses',
    'outdoor_uses',
    'benefits',
    'ai_summary',
    'technology',
    'box_contents',
];

const sortSections = (sections) => {
    const ordered   = SECTION_ORDER.map(k => sections.find(s => s.section === k)).filter(Boolean);
    const remaining = sections.filter(s => !SECTION_ORDER.includes(s.section) && s.section !== 'hero');
    const hero      = sections.filter(s => s.section === 'hero');
    return [...ordered, ...remaining, ...hero];
};

/* ── section dispatcher ───────────────────────────────────────────────────── */
const renderSection = (s, i, slug) => {
    const key = `${s.section}-${i}`;
    switch (s.section) {
        case 'hero':               return <HeroSection              key={key} s={s} />;
        case 'feature_highlights': return <FeatureHighlightsSection key={key} s={s} />;
        case 'overview':           return <OverviewSection          key={key} s={s} />;
        case 'key_features':       return <KeyFeaturesSection       key={key} s={s} />;
        case 'indoor_uses':        return <UsesSection              key={key} s={s} icon="pi-home" />;
        case 'outdoor_uses':       return <UsesSection              key={key} s={s} icon="pi-map-marker" />;
        case 'benefits':           return <BenefitsSection          key={key} s={s} />;
        case 'technology':         return <TechnologySection         key={key} s={s} />;
        case 'best_fit_for':       return <BestFitSection           key={key} s={s} />;
        case 'box_contents':       return <BoxContentsSection       key={key} s={s} />;
        case 'user_manual':        return <UserManualSection        key={key} s={s} slug={slug} />;
        case 'why_choose_ennix':   return <WhyChooseSection         key={key} s={s} />;
        case 'quick_verdict':      return <VerdictSection           key={key} s={s} />;
        case 'ai_summary':         return <AISummarySection         key={key} s={s} />;
        default:                   return <DefaultSection           key={key} s={s} />;
    }
};

/* ── Reusable animated AI icon visual ────────────────────────────────────── */

/* ── Section navigation sidebar ─────────────────────────────────────────── */
const SectionNav = ({ items, activeIdx, onJump }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '0.4rem', padding: '0 0.55rem', flexShrink: 0,
        borderLeft: '1px solid #f1f5f9', width: 24, overflowY: 'auto',
    }}>
        {items.map((item, i) => {
            const active = activeIdx === i;
            return (
                <button key={i} onClick={() => onJump(i)} title={item.title} style={{
                    width: active ? 14 : 8,
                    height: active ? 6 : 4,
                    borderRadius: '999px', flexShrink: 0,
                    background: active ? 'var(--primary-color)' : '#cbd5e1',
                    border: 'none', cursor: 'pointer', padding: 0,
                    transition: 'all 0.2s',
                }} />
            );
        })}
    </div>
);

/* ── Modal ───────────────────────────────────────────────────────────────── */
const getAuthUser = () => {
    try {
        const match = document.cookie.match(/(?:^|;\s*)authUser=([^;]*)/);
        if (!match) return null;
        return JSON.parse(decodeURIComponent(match[1]));
    } catch { return null; }
};

const AIModal = ({ title, coverImage, productId, price, mrp, offer, coupons, product, onClose }) => {
    const [sections,       setSections]       = useState([]);
    const [summaryTitle,   setSummaryTitle]   = useState('');
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState(null);
    const [codCharges,     setCodCharges]     = useState(0);
    const [prepaidDiscount,setPrepaidDiscount]= useState(0);
    const [activeSecIdx,   setActiveSecIdx]   = useState(0);
    const [filterOpen,     setFilterOpen]     = useState(false);
    const bodyRef                             = useRef(null);
    const sectionRefs                         = useRef([]);

    /* question */
    /* feedback */
    const [fbOpen,       setFbOpen]       = useState(false);
    const [fbDone,       setFbDone]       = useState(false);
    const [fbChoice,     setFbChoice]     = useState(null);
    const [fbMsg,        setFbMsg]        = useState('');
    const [fbRating,     setFbRating]     = useState(0);
    const [fbName,       setFbName]       = useState('');
    const [fbEmail,      setFbEmail]      = useState('');
    const [fbError,      setFbError]      = useState(null);
    const [fbSubmitting, setFbSubmitting] = useState(false);

    const submitFeedback = async () => {
        setFbSubmitting(true);
        setFbError(null);
        try {
            await fetch('/api/ai/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId,
                    rating:  fbRating  || null,
                    choice:  fbChoice  || null,
                    message: fbMsg.trim()   || null,
                    name:    fbName.trim()  || null,
                    email:   fbEmail.trim() || null,
                }),
            });
        } catch (_) {}
        setFbSubmitting(false);
        setFbDone(true);
    };

    /* question */
    const [qOpen,       setQOpen]       = useState(false);
    const [qText,       setQText]       = useState('');
    const [qName,       setQName]       = useState('');
    const [qEmail,      setQEmail]      = useState('');
    const [qSubmitting, setQSubmitting] = useState(false);
    const [qDone,       setQDone]       = useState(false);
    const [qError,      setQError]      = useState(null);

    const submitQuestion = async () => {
        if (!qText.trim()) { setQError('Please enter your question.'); return; }
        if (!qEmail.trim()) { setQError('Email is required — we need it to reply to you.'); return; }
        setQSubmitting(true);
        setQError(null);
        const payload = {
            question: qText.trim(),
            clientInfo: {
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
                referer:   typeof document  !== 'undefined' ? (document.referrer || window.location.href) : null,
                productId,
            },
        };
        if (qName.trim())  payload.name  = qName.trim();
        if (qEmail.trim()) payload.email = qEmail.trim();
        const res = await submitProductQuestionAction(productId, payload);
        setQSubmitting(false);
        if (res.success) setQDone(true);
        else setQError(res.error || 'Failed to submit. Try again.');
    };

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
    }, [onClose]);

    useEffect(() => {
        const user = getAuthUser();
        if (!user) return;
        const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || '';
        const email = user.email || '';
        if (name)  { setFbName(name);  setQName(name);  }
        if (email) { setFbEmail(email); setQEmail(email); }
    }, []);

    useEffect(() => {
        fetchStoreConfigAction().then(cfg => {
            if (!cfg) return;
            setCodCharges(cfg.codCharges ?? 0);
            setPrepaidDiscount(cfg.prepaidDiscount ?? 0);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        if (!productId) { setLoading(false); setError('No product ID.'); return; }
        let ignore = false;
        setLoading(true);
        setError(null);
        setSections([]);
        getProductSummary(productId)
            .then((json) => {
                console.log('Product summary:', json);
                if (ignore) return;
                if (json.success && json.data?.content) {
                    setSummaryTitle(json.data.content[0]?.title || '');
                    const manualSection = json.data.userManualUrl
                        ? [{ section: 'user_manual', title: 'User Manual', content: json.data.userManualUrl }]
                        : [];
                    setSections([...json.data.content, ...manualSection]);
                } else {
                    setError(json.error || 'Could not load summary.');
                }
            })
            .catch(() => { if (!ignore) setError('Something went wrong. Please try again.'); })
            .finally(() => { if (!ignore) setLoading(false); });
        return () => { ignore = true; };
    }, [productId]);

    useEffect(() => {
        const container = bodyRef.current;
        if (!container || !sections.length) return;
        const onScroll = () => {
            const containerTop = container.getBoundingClientRect().top;
            let found = 0;
            sectionRefs.current.forEach((el, i) => {
                if (el && el.getBoundingClientRect().top - containerTop <= 32) found = i;
            });
            setActiveSecIdx(found);
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [sections]);

    const jumpTo = (idx) => {
        const el        = sectionRefs.current[idx];
        const container = bodyRef.current;
        if (!el || !container) return;
        const elTop        = el.getBoundingClientRect().top;
        const containerTop = container.getBoundingClientRect().top;
        container.scrollTo({ top: container.scrollTop + (elTop - containerTop), behavior: 'smooth' });
    };

    const sortedSecs = sortSections(sections);
    const navItems   = [
        ...(price ? [{ title: 'Pricing', section: 'pricing' }] : []),
        ...sortedSecs.map(s => ({ title: s.title, section: s.section })),
    ];

    return createPortal(
        <>
            <style>{`
                @keyframes ai-modal-in {
                    from { opacity: 0; transform: scale(0.92) translateY(24px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }
                @keyframes ai-dot-bounce {
                    0%, 80%, 100% { transform: translateY(0);    opacity: 0.4; }
                    40%           { transform: translateY(-6px); opacity: 1;   }
                }
                @keyframes ai-fb-slide-in {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .ai-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: var(--primary-color); animation: ai-dot-bounce 1.2s ease-in-out infinite; }
                .ai-dot:nth-child(2) { animation-delay: 0.2s; }
                .ai-dot:nth-child(3) { animation-delay: 0.4s; }
                .ai-fb-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 14px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; cursor: pointer; border: 1.5px solid #e2e8f0; background: #f8fafc; color: #475569; transition: all 0.15s; }
                .ai-fb-btn:hover { border-color: var(--primary-color); color: var(--primary-color); }
                .ai-fb-btn.yes.active { background: #f0fdf4; border-color: #22c55e; color: #16a34a; }
                .ai-fb-btn.no.active  { background: #fff1f2; border-color: #f43f5e; color: #e11d48; }
                .ai-fb-input { width: 100%; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 10px; font-size: 0.82rem; color: #1e293b; background: #fff; outline: none; font-family: inherit; resize: none; box-sizing: border-box; transition: border-color 0.15s; }
                .ai-fb-input:focus { border-color: var(--primary-color); }
                .ai-star { font-size: 1.1rem; cursor: pointer; color: #cbd5e1; transition: color 0.12s; }
                .ai-star.lit { color: #f59e0b; }
                .ai-hdr-btns .p-button { background: transparent !important; border: none !important; color: #64748b !important; flex-direction: column !important; gap: 2px !important; padding: 4px 8px !important; border-radius: 8px !important; height: auto !important; min-width: 44px; line-height: 1; box-shadow: none !important; }
                .ai-hdr-btns .p-button .p-button-icon { font-size: 0.8rem !important; margin: 0 !important; order: 1 !important; }
                .ai-hdr-btns .p-button .p-button-label { font-size: 0.58rem !important; white-space: nowrap; font-weight: 600; order: 2 !important; }
                .ai-hdr-btns .p-button.bg-green-500 .p-button-label,
                .ai-hdr-btns .p-button.bg-green-500 .p-button-icon { color: #16a34a !important; }
                .ai-hdr-btns .p-button.bg-primary .p-button-label,
                .ai-hdr-btns .p-button.bg-primary .p-button-icon { color: var(--primary-color) !important; }
                .ai-hdr-btns .p-button:hover { background: #f1f5f9 !important; color: #0b3163 !important; border-radius: 8px !important; }
                @keyframes ai-ring-spin  { 0% { transform: rotate(0deg);   } 100% { transform: rotate(360deg);  } }
                @keyframes ai-ring2-spin { 0% { transform: rotate(0deg);   } 100% { transform: rotate(-360deg); } }
                @keyframes ai-pulse-glow {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(11,49,99,0.35), 0 2px 10px rgba(11,49,99,0.25); }
                    50%      { box-shadow: 0 0 0 6px rgba(11,49,99,0),  0 4px 18px rgba(11,49,99,0.4);  }
                }
                @keyframes ai-spin-border { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .ai-modal-frame { position: relative; width: 100%; max-width: 560px; border-radius: 26px; padding: 4px; overflow: hidden; display: flex; flex-direction: column; max-height: 88vh; }
                .ai-modal-frame::before { content: ''; position: absolute; inset: -100%; background: conic-gradient(var(--primary-color) 0deg, #60a5fa 90deg, #a78bfa 180deg, #f472b6 270deg, var(--primary-color) 360deg); animation: ai-spin-border 3s linear infinite; }
                .ai-modal-inner { position: relative; z-index: 1; background: #fff; border-radius: 22px; display: flex; flex-direction: column; overflow: hidden; max-height: calc(88vh - 8px); box-shadow: 0 24px 80px rgba(11,49,99,0.28), 0 6px 24px rgba(11,49,99,0.14); animation: ai-modal-in 0.32s cubic-bezier(0.22,1,0.36,1); }
                @media (max-width: 640px) {
                    .ai-modal-frame { max-height: calc(100svh - 5rem); }
                    .ai-modal-inner { max-height: calc(100svh - 5rem - 8px); }
                }
            `}</style>

            {/* Backdrop */}
            <div onClick={onClose} style={{
                position: 'fixed', inset: 0, zIndex: 2000,
                background: 'rgba(0,0,0,0.18)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
            }} />

            {/* Panel */}
            <div style={{
                position: 'fixed', inset: 0, zIndex: 2001,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
                paddingTop:    'max(1rem, env(safe-area-inset-top, 1rem))',
                paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
                pointerEvents: 'none',
            }}>
                <div className="ai-modal-frame" onClick={(e) => e.stopPropagation()} style={{ pointerEvents: 'all' }}>
                <div className="ai-modal-inner">
                    {/* ── Header bar ──────────────────────────────────────── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.8rem 1rem 0.7rem 1.25rem',
                        borderBottom: '1px solid #f1f5f9', flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                            <AnimatedLogoIcon size={28} />
                            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--primary-color)', letterSpacing: '0.01em' }}>
                                Product Summary
                            </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            {product && !product.outOfStock && (
                                <div className="ai-hdr-btns" style={{ display: 'flex', gap: '0.35rem' }}>
                                    <AddToCart product={product} />
                                    <BuyNowButton product={product} />
                                </div>
                            )}
                            <button onClick={onClose} aria-label="Close" style={{
                                width: 30, height: 30, borderRadius: '50%',
                                border: '1px solid #e2e8f0', background: '#f8fafc',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#64748b', flexShrink: 0,
                            }}>
                                <i className="pi pi-times" style={{ fontSize: '0.65rem' }} />
                            </button>
                        </div>
                    </div>

                    {/* ── Summary title highlight ──────────────────────────── */}
                    {summaryTitle && (
                        <div style={{ position: 'relative', margin: '0.75rem 1.25rem 0', flexShrink: 0 }}>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.45rem 0.6rem 0.45rem 1rem',
                                background: 'rgba(11,49,99,0.05)',
                                border: '1px solid rgba(11,49,99,0.1)',
                                borderLeft: '3px solid var(--primary-color)',
                                borderRadius: '0 8px 8px 0',
                            }}>
                                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--primary-color)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                    {summaryTitle}
                                </span>
                                {navItems.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setFilterOpen(v => !v)}
                                        title="Jump to section"
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '0.25rem',
                                            background: filterOpen ? 'var(--primary-color)' : 'transparent',
                                            border: '1px solid rgba(11,49,99,0.2)', borderRadius: '6px',
                                            padding: '3px 8px', cursor: 'pointer', flexShrink: 0,
                                            color: filterOpen ? '#fff' : 'var(--primary-color)',
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        <i className="pi pi-filter" style={{ fontSize: '0.6rem' }} />
                                        <span style={{ fontSize: '0.6rem', fontWeight: 600 }}>Filter</span>
                                    </button>
                                )}
                            </div>

                            {/* ── Section filter dropdown ── */}
                            {filterOpen && navItems.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 20,
                                    background: '#fff', border: '1px solid #e2e8f0',
                                    borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                    padding: '0.4rem', minWidth: 200, maxHeight: 260, overflowY: 'auto',
                                }}>
                                    {navItems.map((item, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={() => { jumpTo(i); setFilterOpen(false); }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                width: '100%', padding: '0.4rem 0.6rem', borderRadius: '6px',
                                                border: 'none', background: activeSecIdx === i ? 'rgba(11,49,99,0.07)' : 'transparent',
                                                cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s',
                                            }}
                                        >
                                            <span style={{
                                                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                                background: activeSecIdx === i ? 'var(--primary-color)' : '#cbd5e1',
                                            }} />
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: activeSecIdx === i ? 700 : 400,
                                                color: activeSecIdx === i ? 'var(--primary-color)' : '#334155',
                                            }}>
                                                {item.title}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Product identity row ─────────────────────────────── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.85rem',
                        padding: '0.75rem 1.25rem', flexShrink: 0,
                    }}>
                        {coverImage && (
                            <div style={{
                                width: 52, height: 52, borderRadius: '10px',
                                background: '#f8fafc', flexShrink: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '1px solid #e2e8f0',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={coverImage} alt={title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                        )}
                        <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35, flex: 1, minWidth: 0 }}>
                            {title}
                        </p>
                    </div>

                    <div style={{ height: 1, background: '#e2e8f0', margin: '0 1.25rem', flexShrink: 0 }} />

                    {/* ── Body ─────────────────────────────────────────────── */}
                    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                        <div ref={bodyRef} style={{ padding: '1rem 1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>

                            {/* Loading */}
                            {loading && (
                                <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '0.85rem 1.1rem' }}>
                                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.83rem', color: '#475569', lineHeight: 1.6 }}>
                                        Generating summary for <strong>{title}</strong>…
                                    </p>
                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                        <span className="ai-dot" />
                                        <span className="ai-dot" />
                                        <span className="ai-dot" />
                                    </div>
                                </div>
                            )}

                            {/* Error */}
                            {!loading && error && (
                                <div style={{
                                    background: '#fff7ed', border: '1px solid #fdba74',
                                    borderRadius: '10px', padding: '0.85rem 1rem',
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                }}>
                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f97316', fontSize: '0.85rem' }} />
                                    <span style={{ fontSize: '0.83rem', color: '#9a3412' }}>{error}</span>
                                </div>
                            )}

                            {/* Sections */}
                            {!loading && !error && sections.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {price && (
                                        <div ref={el => { sectionRefs.current[0] = el; }}>
                                            <PricingSection price={price} mrp={mrp} offer={offer} coupons={coupons} codCharges={codCharges} prepaidDiscount={prepaidDiscount} warranty={product?.warranty} />
                                        </div>
                                    )}
                                    {sortedSecs.map((s, i) => (
                                        <div key={`sr-${s.section}-${i}`} ref={el => { sectionRefs.current[price ? i + 1 : i] = el; }}>
                                            {renderSection(s, i, product?.slug)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Section nav */}
                        {!loading && !error && navItems.length > 0 && (
                            <SectionNav items={navItems} activeIdx={activeSecIdx} onJump={jumpTo} />
                        )}
                    </div>

                    {/* ── Feedback overlay panel ───────────────────────────── */}
                    {fbOpen && (
                        <div style={{
                            margin: '0 1.25rem',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '14px',
                            padding: '1rem 1.1rem',
                            flexShrink: 0,
                            animation: 'ai-fb-slide-in 0.22s ease',
                        }}>
                            {fbDone ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="pi pi-check-circle" style={{ color: '#22c55e', fontSize: '1rem' }} />
                                        <span style={{ fontSize: '0.83rem', fontWeight: 600, color: '#16a34a' }}>Thank you for your feedback!</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setFbOpen(false); setFbChoice(null); setFbDone(false); setFbMsg(''); setFbRating(0); setFbName(''); setFbEmail(''); }}
                                        aria-label="Close"
                                        style={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}
                                    >
                                        <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    {/* Stars row + close */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                            <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: 2 }}>Rate:</span>
                                            {[1,2,3,4,5].map(n => (
                                                <span
                                                    key={n}
                                                    className={`ai-star${n <= fbRating ? ' lit' : ''}`}
                                                    onClick={() => setFbRating(n === fbRating ? 0 : n)}
                                                    role="button"
                                                    aria-label={`${n} star`}
                                                >★</span>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setFbOpen(false); setFbChoice(null); setFbMsg(''); setFbRating(0); setFbName(''); setFbEmail(''); setFbError(null); }}
                                            aria-label="Close feedback"
                                            style={{
                                                width: 24, height: 24, borderRadius: '50%',
                                                border: '1px solid #e2e8f0', background: '#fff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', color: '#94a3b8', flexShrink: 0,
                                            }}
                                        >
                                            <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
                                        </button>
                                    </div>

                                    <textarea
                                        className="ai-fb-input"
                                        rows={2}
                                        placeholder="Share your thoughts… (optional)"
                                        value={fbMsg}
                                        onChange={e => setFbMsg(e.target.value)}
                                    />

                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <input className="ai-fb-input" placeholder="Name (optional)" value={fbName} onChange={e => setFbName(e.target.value)} />
                                        <input className="ai-fb-input" placeholder="Email (optional)" type="email" value={fbEmail} onChange={e => setFbEmail(e.target.value)} />
                                    </div>

                                    {fbError && <p style={{ margin: 0, fontSize: '0.75rem', color: '#e11d48' }}>{fbError}</p>}

                                    <button
                                        type="button"
                                        onClick={submitFeedback}
                                        disabled={fbSubmitting}
                                        style={{
                                            alignSelf: 'flex-end',
                                            padding: '6px 18px', borderRadius: '20px',
                                            border: 'none', background: 'var(--primary-color)',
                                            color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                                            cursor: fbSubmitting ? 'not-allowed' : 'pointer',
                                            opacity: fbSubmitting ? 0.7 : 1,
                                            display: 'flex', alignItems: 'center', gap: '5px',
                                        }}
                                    >
                                        {fbSubmitting && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />}
                                        Submit
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Question overlay panel ───────────────────────────── */}
                    {qOpen && (
                        <div style={{
                            margin: '0 1.25rem',
                            background: '#f8fafc', border: '1px solid #e2e8f0',
                            borderRadius: '14px', padding: '1rem 1.1rem',
                            flexShrink: 0, animation: 'ai-fb-slide-in 0.22s ease',
                        }}>
                            {qDone ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="pi pi-check-circle" style={{ color: '#22c55e', fontSize: '1rem' }} />
                                        <span style={{ fontSize: '0.83rem', fontWeight: 600, color: '#16a34a' }}>Question submitted! We'll get back to you.</span>
                                    </div>
                                    <button type="button" onClick={() => { setQOpen(false); setQDone(false); setQText(''); setQName(''); setQEmail(''); setQError(null); }}
                                        style={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}>
                                        <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                            <i className="pi pi-question-circle" style={{ fontSize: '0.8rem', color: 'var(--primary-color)' }} />
                                            Have a query?
                                        </span>
                                        <button type="button" onClick={() => { setQOpen(false); setQText(''); setQName(''); setQEmail(''); setQError(null); }}
                                            style={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#94a3b8', flexShrink: 0 }}>
                                            <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
                                        </button>
                                    </div>
                                    <textarea
                                        className="ai-fb-input"
                                        rows={3}
                                        placeholder="Type your question… (required)"
                                        value={qText}
                                        onChange={e => setQText(e.target.value)}
                                    />
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                        <input className="ai-fb-input" style={{ flex: 1 }} placeholder="Name (optional)" value={qName} onChange={e => setQName(e.target.value)} />
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                            <input className="ai-fb-input" placeholder="Email (required)" type="email" value={qEmail} onChange={e => setQEmail(e.target.value)} required />
                                            <p style={{ margin: 0, fontSize: '0.62rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                <i className="pi pi-send" style={{ fontSize: '0.58rem', color: 'var(--primary-color)' }} />
                                                We'll reply on this email
                                            </p>
                                        </div>
                                    </div>
                                    {qError && <p style={{ margin: 0, fontSize: '0.75rem', color: '#e11d48' }}>{qError}</p>}
                                    <button type="button" onClick={submitQuestion} disabled={qSubmitting} style={{
                                        alignSelf: 'flex-end', padding: '6px 18px', borderRadius: '20px',
                                        border: 'none', background: 'var(--primary-color)', color: '#fff',
                                        fontSize: '0.8rem', fontWeight: 600,
                                        cursor: qSubmitting ? 'not-allowed' : 'pointer', opacity: qSubmitting ? 0.7 : 1,
                                        display: 'flex', alignItems: 'center', gap: '5px',
                                    }}>
                                        {qSubmitting && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />}
                                        Submit
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Footer ───────────────────────────────────────────── */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.65rem 1.25rem 0.85rem',
                        borderTop: '1px solid #f1f5f9',
                        flexShrink: 0, marginTop: '0.5rem', gap: '0.75rem',
                    }}>
                        {/* Left — Was this helpful? */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                Was this helpful?
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    if (fbOpen && fbChoice === 'yes') { setFbOpen(false); }
                                    else { setFbOpen(true); setFbChoice('yes'); setFbRating(5); setFbDone(false); setFbError(null); setQOpen(false); }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 11px', borderRadius: '20px',
                                    background: fbOpen && fbChoice === 'yes' ? '#dcfce7' : 'rgba(16,185,129,0.07)',
                                    border: `1.5px solid ${fbOpen && fbChoice === 'yes' ? '#34d399' : 'rgba(16,185,129,0.25)'}`,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <i className="pi pi-thumbs-up" style={{ fontSize: '0.72rem', color: '#10b981' }} />
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669' }}>Yes</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    if (fbOpen && fbChoice === 'no') { setFbOpen(false); }
                                    else { setFbOpen(true); setFbChoice('no'); setFbRating(2); setFbDone(false); setFbError(null); setQOpen(false); }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '4px',
                                    padding: '4px 11px', borderRadius: '20px',
                                    background: fbOpen && fbChoice === 'no' ? '#fee2e2' : 'rgba(239,68,68,0.07)',
                                    border: `1.5px solid ${fbOpen && fbChoice === 'no' ? '#fca5a5' : 'rgba(239,68,68,0.25)'}`,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <i className="pi pi-thumbs-down" style={{ fontSize: '0.72rem', color: '#ef4444' }} />
                                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#dc2626' }}>No</span>
                            </button>
                        </div>

                        {/* Right — Have a question? */}
                        <button
                            type="button"
                            onClick={() => { setQOpen(v => !v); setQDone(false); setQError(null); setFbOpen(false); }}
                            style={{
                                background: 'none', border: 'none', padding: 0,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px',
                                flexShrink: 0,
                            }}
                        >
                            <span style={{
                                fontSize: '0.72rem', fontWeight: 600,
                                color: qOpen ? 'var(--primary-color)' : '#94a3b8',
                                textDecoration: qOpen ? 'underline' : 'none',
                                transition: 'color 0.15s',
                                whiteSpace: 'nowrap',
                            }}>
                                Have a question
                            </span>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 16, height: 16, borderRadius: '50%',
                                background: qOpen ? 'var(--primary-color)' : '#e2e8f0',
                                fontSize: '0.6rem', fontWeight: 800,
                                color: qOpen ? '#fff' : '#64748b',
                                transition: 'all 0.15s', flexShrink: 0,
                            }}>?</span>
                        </button>
                    </div>
                </div>{/* ai-modal-inner */}
                </div>{/* ai-modal-frame */}
            </div>
        </>,
        document.body
    );
};

/* ── Animated AI circle button ────────────────────────────────────────────── */
export const AICircle = ({ title = 'Ennix AI', coverImage, productId, price, mrp, offer, coupons, product, size = 38 }) => {
    const [open,    setOpen]    = useState(false);
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Open AI summary"
                style={{
                    background: 'none', border: 'none',
                    cursor: 'pointer', padding: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
            >
                <AnimatedLogoIcon size={40} />
            </button>

            {mounted && open && (
                <AIModal title={title} coverImage={coverImage} productId={productId} price={price} mrp={mrp} offer={offer} coupons={coupons} product={product} onClose={() => setOpen(false)} />
            )}
        </>
    );
};
